import { useState, useMemo } from "react";
import {
  Box,
  Typography,
  Dialog,
  DialogContent,
  IconButton,
  Divider,
  Button,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import PhotoCaptureModal from "./PhotoCaptureModal";
import good from "../../assets/good.jpg";
import blur from "../../assets/blur.jpg";
import frontview from "../../assets/frontview.jpg";
import sideview from "../../assets/sideview.jpg";
import wideview from "../../assets/wideview.jpg";
import frontviewblur from "../../assets/frontviewblur.jpg";
import sideviewblur from "../../assets/sideviewblur.jpg";
import wideviewblur from "../../assets/wideblur.jpg";

const styles = {
  dialog: {
    "& .MuiDialog-paper": {
      borderRadius: "12px",
      maxWidth: "500px",
      margin: "16px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
    },
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "16px 20px",
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#f9f9f9",
  },
  title: {
    fontSize: "18px",
    fontWeight: 700,
    color: "#000",
    fontFamily: "Roboto, sans-serif",
  },
  content: {
    padding: "20px",
    backgroundColor: "#fff",
  },
  section: {
    marginBottom: "20px",
  },
  sectionTitle: {
    fontSize: "16px",
    fontWeight: 600,
    color: "#000",
    marginBottom: "8px",
    fontFamily: "Roboto, sans-serif",
  },
  list: {
    paddingLeft: "20px",
    margin: "8px 0",
  },
  listItem: {
    fontSize: "14px",
    color: "#333",
    marginBottom: "4px",
    lineHeight: "1.4",
    fontFamily: "Roboto, sans-serif",
  },
  imageContainer: {
    width: "100%",
    marginBottom: "16px",
    borderRadius: "8px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "space-between",
  },
  image: {
    width: "48%",
    height: "auto",
    display: "block",
  },
  takePhotoButton: {
    backgroundColor: "#6f2fa8",
    color: "#fff",
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: "14px",
    padding: "12px 24px",
    borderRadius: "8px",
    width: "100%",
    "&:hover": {
      backgroundColor: "#5a2380",
    },
    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
  },
  retakeInfo: {
    backgroundColor: "#f3f4f6",
    padding: "12px",
    borderRadius: "8px",
    marginBottom: "16px",
    border: "1px solid #e5e7eb",
  },
  retakeText: {
    fontSize: "14px",
    color: "#374151",
    fontWeight: 500,
    textAlign: "center" as const,
  },
  photoCaptionHighlight: {
    color: "#6f2fa8",
    fontWeight: 600,
  },
};

interface PhotoGuidelinesModalProps {
  open: boolean;
  onClose: () => void;
  onPhotosTaken: (photos: string[]) => void;
  isRetakeMode?: boolean;
  retakeIndex?: number | null;
  retakeCaption?: string;
  currentPhotos?: (string | null | undefined)[];
}

export default function PhotoGuidelinesModal({
  open,
  onClose,
  onPhotosTaken,
  isRetakeMode = false,
  retakeIndex = null,
  retakeCaption = "",
  currentPhotos = [],
}: PhotoGuidelinesModalProps) {
  const [showCamera, setShowCamera] = useState(false);
  const photoCaptions = ["Front View", "Side View", "Wide View"];

  const handleTakePhoto = () => {
    setShowCamera(true);
  };

  const handleCameraClose = () => {
    setShowCamera(false);
    onClose();
  };

  // ✅ Select correct image pair dynamically
  const { goodImage, blurImage } = useMemo(() => {
    const caption = retakeCaption.toLowerCase();
    if (caption.includes("front")) {
      return { goodImage: frontview, blurImage: frontviewblur };
    } else if (caption.includes("side")) {
      return { goodImage: sideview, blurImage: sideviewblur };
    } else if (caption.includes("wide")) {
      return { goodImage: wideview, blurImage: wideviewblur };
    } else {
      // fallback
      return { goodImage: good, blurImage: blur };
    }
  }, [retakeCaption]);

  const getModalTitle = () => {
    if (isRetakeMode && retakeCaption) {
      return `Take ${retakeCaption}`;
    }
    return "Photo Guidelines";
  };

  const getButtonText = () => {
    if (isRetakeMode) {
      return `Take ${retakeCaption}`;
    }
    if (currentPhotos.length === 0) {
      return "Take Photos";
    }
    return `Add More Photos (${currentPhotos.length}/3)`;
  };

  const getMaxPhotos = () => {
    if (isRetakeMode) return 1;
    return Math.max(1, 3 - currentPhotos.length);
  };

  const getCaptionsForCamera = () => {
    if (isRetakeMode && retakeIndex !== null) {
      return [photoCaptions[retakeIndex] || `Photo ${retakeIndex + 1}`];
    }

    const missingCaptions = [];
    for (let i = currentPhotos.length; i < 3; i++) {
      missingCaptions.push(photoCaptions[i] || `Photo ${i + 1}`);
    }
    return missingCaptions;
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} sx={styles.dialog}>
        <Box sx={styles.header}>
          <Typography sx={styles.title}>{getModalTitle()}</Typography>
          <IconButton onClick={onClose} size="small" sx={{ color: "#000" }}>
            <CloseIcon />
          </IconButton>
        </Box>

        <DialogContent sx={styles.content}>
          {isRetakeMode && retakeCaption && (
            <Box sx={styles.retakeInfo}>
              <Typography sx={styles.retakeText}>
                You are taking the{" "}
                <span style={styles.photoCaptionHighlight}>{retakeCaption}</span>{" "}
                photo. Follow the guidelines below for the best result.
              </Typography>
            </Box>
          )}

          {!isRetakeMode && currentPhotos.length > 0 && (
            <Box sx={styles.retakeInfo}>
              <Typography sx={styles.retakeText}>
                You have{" "}
                <span style={styles.photoCaptionHighlight}>
                  {currentPhotos.length} of 3
                </span>{" "}
                photos. Take {3 - currentPhotos.length} more photo
                {3 - currentPhotos.length !== 1 ? "s" : ""} to complete.
              </Typography>
            </Box>
          )}

          <Box sx={styles.imageContainer}>
            <img src={goodImage} alt="Good Photo Angle" style={styles.image} />
            <img src={blurImage} alt="Poor Photo Angle" style={styles.image} />
          </Box>

          {/* Guidelines Text */}
          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Good Photo Angle</Typography>
            <Box component="ul" sx={styles.list}>
              <Box component="li" sx={styles.listItem}>
                Stand directly in front of the accessibility feature (e.g.,
                entrance, ramp, lift).
              </Box>
              <Box component="li" sx={styles.listItem}>
                Hold the phone steady at chest height for a clear shot.
              </Box>
              <Box component="li" sx={styles.listItem}>
                Ensure no obstructions like people or vehicles block the view.
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={styles.section}>
            <Typography sx={styles.sectionTitle}>Poor Photo Angle</Typography>
            <Box component="ul" sx={styles.list}>
              <Box component="li" sx={styles.listItem}>
                Avoid blurry or obstructed views.
              </Box>
              <Box component="li" sx={styles.listItem}>
                Do not hold the phone at an awkward angle.
              </Box>
              <Box component="li" sx={styles.listItem}>
                Ensure proper lighting to avoid dark shots.
              </Box>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: "center", mt: 3 }}>
            <Button sx={styles.takePhotoButton} onClick={handleTakePhoto}>
              {getButtonText()}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Photo capture camera */}
      <PhotoCaptureModal
        open={showCamera}
        onClose={handleCameraClose}
        onPhotosTaken={(photos) => {
          onPhotosTaken(photos as string[]);
          setShowCamera(false);
          onClose();
        }}
        maxPhotos={getMaxPhotos()}
        allowVideo={false}
        photoCaptions={getCaptionsForCamera()}
        mode="photo"
      />
    </>
  );
}
